import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiFetch } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";

interface EsignConfig {
  enabled: boolean;
  required: boolean;
  endpoint: string | null;
  hasToken: boolean;
  evidence: {
    id: string;
    entityType: string;
    entityId: string;
    providerRef: string | null;
    status: string;
    createdAt: string;
  }[];
}

export function SettingsEsignPage() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["esign-settings"],
    queryFn: () => apiFetch<EsignConfig>("/api/v1/admin/settings/esign"),
  });
  const [enabled, setEnabled] = useState(false);
  const [required, setRequired] = useState(false);
  const [endpoint, setEndpoint] = useState("");
  const [token, setToken] = useState("");
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (query.data && !initialized) {
      setEnabled(query.data.enabled);
      setRequired(query.data.required);
      setEndpoint(query.data.endpoint ?? "");
      setInitialized(true);
    }
  }, [query.data, initialized]);

  async function save() {
    try {
      await apiFetch("/api/v1/admin/settings/esign", {
        method: "PUT",
        body: JSON.stringify({
          enabled,
          required,
          endpoint: endpoint || null,
          token: token || null,
        }),
      });
      await queryClient.invalidateQueries({ queryKey: ["esign-settings"] });
      toast.success("电子签章设置已保存");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "保存失败");
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="电子签章"
        description="配置外部电子签章服务；交接确认时将签名与文档哈希提交，证据写入审计链。"
        actions={<Button onClick={save}>保存设置</Button>}
      />

      <Alert variant="warning" title="合规边界">
        当前集成的是可配置的外部签章 HTTP
        服务；系统内保留签名、文档哈希与提交回执，但法律效力取决于所选服务商的资质与合同。
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>签章服务</CardTitle>
          <CardDescription>启用后，交接确认必须手写签名并提交服务。</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => setEnabled(event.target.checked)}
            />
            启用外部电子签章
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={required}
              onChange={(event) => setRequired(event.target.checked)}
            />
            签章失败时阻止交接确认
          </label>
          <div className="space-y-2">
            <Label>签章服务 URL</Label>
            <Input
              value={endpoint}
              onChange={(event) => setEndpoint(event.target.value)}
              placeholder="https://esign.example.com/api/sign"
            />
          </div>
          <div className="space-y-2">
            <Label>Token {query.data?.hasToken ? "（已保存，留空不修改）" : ""}</Label>
            <Input
              type="password"
              value={token}
              onChange={(event) => setToken(event.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>签章证据</CardTitle>
          <CardDescription>最近 30 条；包含文档哈希、签名哈希与服务回执。</CardDescription>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (query.data?.evidence ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">暂无签章证据。</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>对象</TableHead>
                  <TableHead>服务回执</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>时间</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(query.data?.evidence ?? []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-xs">
                      {row.entityType} / {row.entityId}
                    </TableCell>
                    <TableCell>{row.providerRef ?? "—"}</TableCell>
                    <TableCell>
                      <Badge variant={row.status === "submitted" ? "success" : "danger"}>
                        {row.status === "submitted" ? "已提交" : row.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
